import { Link } from 'react-router-dom';
import styles from './Homepage.module.css';

const ASSETS = {
  hero: '/homepage/v2/hero-desktop-final.webp',
  diagnostic: '/homepage/v2/card-diagnostic-final.webp',
  etapes: '/homepage/v2/card-15-etapes-final.webp',
  videos: '/homepage/v2/card-videos-final.webp',
  musiques: '/homepage/v2/card-musiques-final.webp',
  horsEcran: '/homepage/v2/card-hors-ecran-final.webp',
  heroesBanner: '/choix-heros/banner-v3.png',
  parents: '/homepage/v2/parents-final.webp',
  schools: '/homepage/v2/schools-final.webp',
  comingSoon: '/homepage/v2/coming-soon-final.webp',
} as const;

const featureCards = [
  { src: ASSETS.diagnostic, alt: 'Diagnostic initial — Pour connaître ton point de départ' },
  { src: ASSETS.etapes, alt: '15 étapes de progression — Un parcours motivant et structuré' },
  { src: ASSETS.videos, alt: 'Vidéos explicatives — Des leçons claires et ludiques' },
  { src: ASSETS.musiques, alt: 'Musiques pédagogiques — Apprendre en rythme' },
  { src: ASSETS.horsEcran, alt: "Activités hors écran — Explorer, créer, s’amuser" },
] as const;

function Homepage(): JSX.Element {
  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <a href="#top" className={styles.brand} aria-label="Mon Temps d'Étude — accueil">
          <span className={styles.brandMark} aria-hidden="true">✦</span>
          <span>
            <strong>Mon Temps d'Étude</strong>
            <small>Apprendre aujourd'hui, construire demain.</small>
          </span>
        </a>

        <nav className={styles.nav} aria-label="Navigation principale">
          <a href="#concept">Le concept</a>
          <a href="#pour-qui">Pour qui ?</a>
          <a href="#fonctionnement">Comment ça marche ?</a>
          <a href="#univers">Univers</a>
          <a href="#contact">Contact</a>
        </nav>

        <div className={styles.headerActions}>
          <Link to="/entree/" className={styles.loginButton}>🔒 Connexion</Link>
          <Link to="/bientot/" className={styles.accessButton}>S'inscrire →</Link>
        </div>
      </header>

      <main id="top">
        <section className={styles.hero} aria-labelledby="hero-title">
          <img className={styles.heroArtwork} src={ASSETS.hero} alt="Univers magique de Mon Temps d'Étude" />
          <div className={styles.heroShade} aria-hidden="true" />
          <div className={styles.heroCopy}>
            <p className={styles.eyebrow}>Mon Temps d'Étude</p>
            <h1 id="hero-title">Apprendre avec plaisir,<br />grandir pour la vie !</h1>
            <p className={styles.heroLead}>
              Un univers magique et bienveillant pour progresser à l'école pas à pas,
              développer sa confiance et révéler tout son potentiel.
            </p>
            <a href="#concept" className={styles.primaryCta}>Découvrir le MTE →</a>
            <ul className={styles.heroBenefits} aria-label="Bénéfices principaux">
              <li><span className={styles.dotPink}>♥</span> Plus de confiance</li>
              <li><span className={styles.dotBlue}>●</span> Des progrès durables</li>
              <li><span className={styles.dotGold}>✦</span> Le plaisir d'apprendre</li>
              <li><span className={styles.dotGreen}>●</span> Des compétences pour la vie</li>
            </ul>
          </div>
        </section>

        <section id="concept" className={styles.featuresSection} aria-label="Aperçu de la plateforme">
          <div id="fonctionnement" className={styles.featureGrid}>
            {featureCards.map((card) => (
              <article className={styles.featureCard} key={card.src}>
                <img src={card.src} alt={card.alt} />
              </article>
            ))}
          </div>
        </section>

        <section id="univers" className={styles.universeSection} aria-labelledby="univers-title">
          <h2 id="univers-title" className={styles.sectionKicker}>L'univers MTE</h2>
          <Link to="/onboarding/choix-heros/" className={styles.heroesBanner} aria-label="Choisis ton héros et fais-le évoluer avec toi !">
            <img src={ASSETS.heroesBanner} alt="Choisis ton héros et fais-le évoluer avec toi ! Le Sábio te présente les six héros" width="2048" height="768" loading="eager" decoding="async" />
          </Link>
          <p className={styles.universeCta}>
            <Link to="/onboarding/choix-heros/" className={styles.universeCtaLink}>Choisir mon héros →</Link>
          </p>
        </section>

        <section id="pour-qui" className={styles.audienceGrid}>
          <article className={styles.artworkCard}><img src={ASSETS.parents} alt="Pour les parents — Un accompagnement bienveillant vers la réussite" /></article>
          <article className={styles.artworkCard}><img src={ASSETS.schools} alt="Pour les enseignants et les écoles — Phase 2 · Bientôt" /></article>
        </section>

        <section id="lancement" className={styles.comingSoon}>
          <img src={ASSETS.comingSoon} alt="Le site est en préparation" />
          <a href="mailto:contact@montempsdetude.fr" className={styles.notifyOverlay} aria-label="Me tenir informé(e)" />
        </section>
      </main>

      <footer id="contact" className={styles.footer}>
        <div className={styles.footerBrand}>
          <span className={styles.footerLogo}>✦</span>
          <div><strong>Mon Temps d'Étude</strong><small>Apprendre aujourd'hui, construire demain.</small></div>
        </div>
        <nav aria-label="Liens de pied de page">
          <a href="#concept">À propos</a><a href="#contact">FAQ</a><a href="mailto:contact@montempsdetude.fr">Contact</a><a href="#contact">Confidentialité</a><a href="#contact">Mentions légales</a>
        </nav>
        <p>© {new Date().getFullYear()} Mon Temps d'Étude · Tous droits réservés.</p>
      </footer>
    </div>
  );
}

export default Homepage;
